import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/shared/i18n';
import type { IPreset } from '@/features/presets/types';

interface PresetCardProps {
  preset: IPreset;
  cover: string;
  published: boolean;
  name: string;
  creator?: { name: string; avatar: string };
  packName?: string;
  isFree?: boolean;
}

const getStatusStyles = (t: (key: string) => string) => ({
  published: {
    bg: 'bg-[#8FD460]/10',
    text: 'text-[#8FD460]',
    label: t('publishedLabel'),
  },
  not_published: {
    bg: 'bg-[#91CAFF]/10',
    text: 'text-[#91CAFF]',
    label: t('notPublishedLabel'),
  },
});

export const PresetCard = memo(function PresetCard({
  preset,
  cover,
  published,
  name,
  creator,
  packName,
  isFree = false,
}: PresetCardProps) {
  const { t } = useTranslation('packs');
  const navigate = useNavigate();
  const status = published ? 'published' : 'not_published';
  const style = getStatusStyles(t)[status];

  const handleClick = useCallback(() => {
    const packId = preset.packId || preset.pack?.id;
    if (preset.id && packId) {
      navigate(`/presets/${preset.id}`, {
        state: { packId, packName },
      });
    }
  }, [navigate, preset, packName]);

  return (
    <article
      onClick={handleClick}
      className="group flex flex-col overflow-hidden rounded-lg border border-white/[0.06] hover:border-white/10 transition-colors cursor-pointer"
    >
      <div className="relative aspect-[4/5] w-full overflow-hidden">
        <img
          src={cover}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          alt={name}
        />
      </div>
      <div className="flex flex-col gap-3 p-4">
        <div className="flex gap-2 flex-wrap">
          <span
            className={`inline-flex h-6 items-center justify-center rounded px-2 text-xs font-medium ${style.bg} ${style.text}`}
          >
            {style.label}
          </span>
          <span
            className={`inline-flex h-6 items-center justify-center rounded px-2 text-xs font-medium ${
              isFree ? 'bg-[#91CAFF]/10 text-[#91CAFF]' : 'bg-amber-500/20 text-amber-400'
            }`}
          >
            {isFree ? t('free') : t('paid')}
          </span>
        </div>
        <div className="text-white">
          <h3 className="font-medium">{name}</h3>
          {creator ? (
            <div className="mt-2 flex items-center gap-2">
              <img
                src={creator.avatar || '/placeholder.svg'}
                alt={creator.name}
                className="h-6 w-6 rounded-full object-cover"
              />
              <p className="text-sm text-white/60">{creator.name}</p>
            </div>
          ) : null}
          {packName && (
            <p className="mt-1 text-xs text-white/40">{packName}</p>
          )}
        </div>
      </div>
    </article>
  );
});
